import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * HeadDeduper — single safety-net owner for head SEO tags.
 *
 * This SPA ships static SEO tags in index.html (so non-JS crawlers always see a
 * description/OG/canonical) AND React-Helmet injects per-page versions on top,
 * which produced DUPLICATE / "Multiple" tags (Screaming Frog / GSC findings).
 *
 * Guarantees on every route (hard load AND client-side navigation):
 *   1. DEDUPE — when Helmet injected a tag (data-rh="true"), the matching static
 *      duplicate is removed so exactly one remains (Helmet's per-page value wins).
 *   2. NEVER ZERO — a snapshot of the original static tags is captured once; if a
 *      managed tag ends up with zero copies (e.g. after an imperative manager
 *      deleted the static fallback on a prior route), the snapshot is re-inserted.
 *   3. FRESH URL — canonical / og:url / twitter:url that are NOT owned by Helmet
 *      are refreshed to the current absolute URL, fixing stale canonicals after
 *      client-side navigation (the inline index.html script only runs once).
 */

const SITE = 'https://fortworthdental.com';

const SELECTOR = [
  'meta[name="description"]',
  'meta[name="keywords"]',
  'meta[name="title"]',
  'meta[property^="og:"]',
  'meta[name="twitter:card"]',
  'meta[name="twitter:title"]',
  'meta[name="twitter:description"]',
  'meta[name="twitter:image"]',
  'meta[name="twitter:url"]',
  'link[rel="canonical"]',
].join(',');

const keyFor = (el) => {
  const tag = el.tagName.toLowerCase();
  if (tag === 'link') return 'link:' + (el.getAttribute('rel') || '');
  const prop = el.getAttribute('property');
  if (prop) return 'meta:property:' + prop;
  const name = el.getAttribute('name');
  if (name) return 'meta:name:' + name;
  return null;
};

// Captured once: a clone of each original static fallback tag, by key.
let snapshot = null;
const captureSnapshot = () => {
  if (snapshot) return;
  snapshot = {};
  document.head.querySelectorAll(SELECTOR).forEach((el) => {
    const k = keyFor(el);
    if (k && !snapshot[k]) snapshot[k] = el.cloneNode(true);
  });
};

const absUrl = (pathname) => {
  let p = pathname || '/';
  if (p !== '/' && p.endsWith('/')) p = p.slice(0, -1);
  return p === '/' ? SITE : SITE + p;
};

const dedupe = () => {
  const groups = {};
  document.head.querySelectorAll(SELECTOR).forEach((el) => {
    const k = keyFor(el);
    if (!k) return;
    (groups[k] = groups[k] || []).push(el);
  });
  Object.values(groups).forEach((list) => {
    if (list.length < 2) return;
    const helmet = list.filter((e) => e.getAttribute('data-rh') === 'true');
    if (helmet.length) {
      list.forEach((e) => { if (e.getAttribute('data-rh') !== 'true') e.remove(); });
      helmet.slice(0, -1).forEach((e) => e.remove());
    } else {
      list.slice(0, -1).forEach((e) => e.remove());
    }
  });
};

const restoreMissing = () => {
  if (!snapshot) return;
  const present = new Set();
  document.head.querySelectorAll(SELECTOR).forEach((el) => {
    const k = keyFor(el);
    if (k) present.add(k);
  });
  Object.entries(snapshot).forEach(([k, node]) => {
    // Don't resurrect og:url / twitter:url / canonical as stale snapshots —
    // those are URL-specific and handled by refreshUrlTags below.
    if (k === 'meta:property:og:url' || k === 'meta:name:twitter:url' || k === 'link:canonical') return;
    if (!present.has(k)) {
      const clone = node.cloneNode(true);
      clone.setAttribute('data-fwd-fallback', 'true');
      document.head.appendChild(clone);
    }
  });
};

const refreshUrlTags = (pathname) => {
  const url = absUrl(pathname);
  // Ensure a canonical exists and points at the current URL (unless Helmet owns it).
  let canonical = document.head.querySelector('link[rel="canonical"][data-rh="true"]');
  if (!canonical) {
    canonical = document.head.querySelector('link[rel="canonical"]:not([data-rh])');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.setAttribute('data-fwd-fallback', 'true');
      document.head.appendChild(canonical);
    }
    canonical.href = url;
  }
  // og:url — ensure one exists pointing at the current URL (unless Helmet owns it).
  if (!document.head.querySelector('meta[property="og:url"][data-rh="true"]')) {
    let ogurl = document.head.querySelector('meta[property="og:url"]:not([data-rh])');
    if (!ogurl) {
      ogurl = document.createElement('meta');
      ogurl.setAttribute('property', 'og:url');
      ogurl.setAttribute('data-fwd-fallback', 'true');
      document.head.appendChild(ogurl);
    }
    ogurl.setAttribute('content', url);
  }
  // twitter:url that isn't owned by Helmet → refresh to current URL.
  document.head
    .querySelectorAll('meta[name="twitter:url"]:not([data-rh])')
    .forEach((el) => el.setAttribute('content', url));
};

const HeadDeduper = () => {
  const location = useLocation();

  useEffect(() => {
    captureSnapshot();
    const path = location.pathname;
    const cycle = () => {
      dedupe();
      restoreMissing();
      refreshUrlTags(path);
    };
    let t;
    const run = () => { clearTimeout(t); t = setTimeout(cycle, 60); };
    const observer = new MutationObserver(run);
    observer.observe(document.head, { childList: true });
    cycle(); // immediate pass
    const late1 = setTimeout(cycle, 600);
    const late2 = setTimeout(cycle, 1500);
    return () => {
      observer.disconnect();
      clearTimeout(t);
      clearTimeout(late1);
      clearTimeout(late2);
    };
  }, [location.pathname]);

  return null;
};

export default HeadDeduper;
